import { useEffect, useState } from 'react';

// Beyond this much obscured bottom space we treat the soft keyboard as open.
// Browser chrome (toolbars) alone obscures well under this; a keyboard is
// typically 250–350px, so the gap cleanly separates the two cases.
const KEYBOARD_MIN_INSET = 150;

/**
 * True while an on-screen (soft) keyboard is probably open. Detected via the
 * visual viewport shrinking far more than browser chrome alone would obscure,
 * which is the most reliable cross-browser signal on mobile web.
 *
 * Always false at lg+ (no soft keyboard) and when the visualViewport API is
 * unavailable. Shared by the floating NavBar and the BackToTop button so both
 * get out of the way while the user is typing in a form.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const update = () => {
      const obscured = window.innerHeight - vv.height - vv.offsetTop;
      setOpen(!desktop.matches && obscured > KEYBOARD_MIN_INSET);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    desktop.addEventListener('change', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      desktop.removeEventListener('change', update);
    };
  }, []);

  return open;
}
